import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQGrid from '../../components/grid';
import UQCard from '../../components/card';
import UQSpacer from '../../components/spacer';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import UQCode from '../../components/code';
import { BREADCRUMB_ROOT } from '../../common/config';

const GridPage = () => {
  const attributes = [
    {
      attribute: 'children',
      type: 'React.ReactNode',
      default: 'undefined',
      description: 'The content to display inside the grid.',
    },
    {
      attribute: 'gap',
      type: 'string',
      default: "'1rem'",
      description: 'The gap between grid items.',
    },
    {
      attribute: 'justify',
      type: "'start' | 'end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'",
      default: "'start'",
      description: 'The alignment of grid items along the main axis.',
    },
    {
      attribute: 'className',
      type: 'string',
      default: "''",
      description: 'Additional class names for custom styling.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  const gridExampleCode = `
import React from 'react';
import { UQGrid, UQCard } from '@uniqconsulting/ui';

<UQGrid gap="1rem" justify="space-around">
  <UQCard>Item 1</UQCard>
  <UQCard>Item 2</UQCard>
  <UQCard>Item 3</UQCard>
</UQGrid>
`;

  const smallElementsCode = `
import React from 'react';
import { UQGrid } from '@uniqconsulting/ui';

<UQGrid gap="0.5rem" justify="start">
  <div style={{ width: '50px', height: '50px', backgroundColor: '#0078D4' }}></div>
  <div style={{ width: '50px', height: '50px', backgroundColor: '#E81123' }}></div>
  <div style={{ width: '50px', height: '50px', backgroundColor: '#107C10' }}></div>
</UQGrid>
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Grid']} />
      <h1>UQGrid Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQGrid</code> component is a flexible layout container that allows you to arrange items in a grid with customizable gaps and alignment. It supports various alignment options for both the main and cross axes.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Examples */}
      <h2>Examples</h2>
      <h3>Grid with Space-Around</h3>
      <UQSpacer size="s" />
      <UQGrid gap="1rem" justify="space-around">
        <UQCard>Item 1</UQCard>
        <UQCard>Item 2</UQCard>
        <UQCard>Item 3</UQCard>
      </UQGrid>
      <UQSpacer size="s" />
      <UQCode code={gridExampleCode} enableCopy />
      <UQSpacer size="m" />

      <h3>Grid with Center Alignment</h3>
      <UQSpacer size="s" />
      <UQGrid gap="1rem" justify="center">
        <UQCard>Item 1</UQCard>
        <UQCard>Item 2</UQCard>
        <UQCard>Item 3</UQCard>
      </UQGrid>
      <UQSpacer size="s" />
      <UQCode
        code={`
import React from 'react';
import { UQGrid, UQCard } from '@uniqconsulting/ui';

<UQGrid gap="1rem" justify="center">
  <UQCard>Item 1</UQCard>
  <UQCard>Item 2</UQCard>
  <UQCard>Item 3</UQCard>
</UQGrid>
`}
        enableCopy
      />
      <UQSpacer size="m" />

      <h3>Grid with Smaller Elements</h3>
      <UQSpacer size="s" />
      <UQGrid gap="0.5rem" justify="start">
        <div style={{ width: '50px', height: '50px', backgroundColor: '#0078D4' }}></div>
        <div style={{ width: '50px', height: '50px', backgroundColor: '#E81123' }}></div>
        <div style={{ width: '50px', height: '50px', backgroundColor: '#107C10' }}></div>
      </UQGrid>
      <UQSpacer size="s" />
      <UQCode code={smallElementsCode} enableCopy />
    </>
  );
};

export default GridPage;